/**
 * Chat Panel Component
 *
 * Direct messaging interface with conversation list and chat view.
 * Supports text messages and media attachments via clipboard.
 */
import { useState, useEffect, useRef, useCallback } from 'react';
import type { Conversation, ChatMessage } from '../hooks/useMessages';
import type { ClipboardEntry } from '@amorphous/core';
import { Avatar } from './Avatar';
import { MarkdownContent } from './MarkdownContent';
import { CallButton } from './CallPanel';
import './ChatPanel.css';

interface ChatPanelProps {
  /** All conversations */
  conversations: Conversation[];
  /** Get messages for a conversation */
  getConversation: (peerPubkey: string) => Promise<ChatMessage[]>;
  /** Send a message */
  sendMessage: (recipientPubkey: string, text: string, options?: { attachments?: string[] }) => Promise<string>;
  /** Mark conversation as read */
  markConversationRead: (peerPubkey: string) => void;
  /** Get display name for a pubkey */
  getDisplayName: (pubkey: string) => string;
  /** Get avatar URL for a pubkey */
  getAvatarUrl?: (pubkey: string) => string | undefined;
  /** Check if a user is online */
  isOnline?: (pubkey: string) => boolean;
  /** Friends set (to filter who can be messaged) */
  friends: Set<string>;
  /** Close the panel */
  onClose: () => void;
  /** Clipboard entries for attachment picker */
  clipboardEntries?: ClipboardEntry[];
  /** Get attachment data URL for preview */
  getAttachmentPreview?: (hash: string) => Promise<string | null>;
  /** Start a call with a peer */
  onStartCall?: (pubkey: string, options: { video?: boolean; audio?: boolean }) => Promise<string | null>;
}

export function ChatPanel({
  conversations,
  getConversation,
  sendMessage,
  markConversationRead,
  getDisplayName,
  getAvatarUrl,
  isOnline,
  friends,
  onClose,
  clipboardEntries = [],
  getAttachmentPreview,
  onStartCall,
}: ChatPanelProps) {
  const [selectedPeer, setSelectedPeer] = useState<string | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [showAttachmentPicker, setShowAttachmentPicker] = useState(false);
  const [selectedAttachments, setSelectedAttachments] = useState<string[]>([]);
  const [attachmentPreviews, setAttachmentPreviews] = useState<Map<string, string>>(new Map());
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load messages when conversation is selected
  useEffect(() => {
    if (!selectedPeer) {
      setMessages([]);
      return;
    }

    const loadMessages = async () => {
      setLoadingMessages(true);
      try {
        const msgs = await getConversation(selectedPeer);
        setMessages(msgs);
        markConversationRead(selectedPeer);
      } catch (err) {
        console.error('[Chat] Failed to load messages:', err);
      } finally {
        setLoadingMessages(false);
      }
    };

    loadMessages();

    // Refresh messages periodically
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [selectedPeer, getConversation, markConversationRead]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Handle sending a message
  const handleSend = useCallback(async () => {
    if (!selectedPeer || (!newMessage.trim() && selectedAttachments.length === 0) || sending) return;

    setSending(true);
    try {
      await sendMessage(selectedPeer, newMessage.trim(), {
        attachments: selectedAttachments.length > 0 ? selectedAttachments : undefined,
      });
      setNewMessage('');
      setSelectedAttachments([]);
      setAttachmentPreviews(new Map());
      // Refresh messages
      const msgs = await getConversation(selectedPeer);
      setMessages(msgs);
    } catch (err) {
      console.error('[Chat] Failed to send:', err);
      alert('Failed to send message');
    } finally {
      setSending(false);
    }
  }, [selectedPeer, newMessage, selectedAttachments, sending, sendMessage, getConversation]);

  // Add attachment from clipboard
  const handleAddAttachment = useCallback(async (entry: ClipboardEntry) => {
    if (selectedAttachments.includes(entry.hash)) return;

    setSelectedAttachments(prev => [...prev, entry.hash]);
    setShowAttachmentPicker(false);

    // Load preview
    if (getAttachmentPreview) {
      const preview = await getAttachmentPreview(entry.hash);
      if (preview) {
        setAttachmentPreviews(prev => new Map(prev).set(entry.hash, preview));
      }
    }
  }, [selectedAttachments, getAttachmentPreview]);

  // Remove attachment
  const handleRemoveAttachment = useCallback((hash: string) => {
    setSelectedAttachments(prev => prev.filter(h => h !== hash));
    setAttachmentPreviews(prev => {
      const next = new Map(prev);
      next.delete(hash);
      return next;
    });
  }, []);

  // Get type icon for clipboard entry
  const getTypeIcon = (type: string) => {
    if (type.startsWith('image/')) return '🖼️';
    if (type.startsWith('video/')) return '🎬';
    if (type.startsWith('audio/')) return '🎵';
    if (type === 'application/pdf') return '📄';
    return '📎';
  };

  // Handle key press in input
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Start new conversation with a friend
  const handleNewConversation = (pubkey: string) => {
    setSelectedPeer(pubkey);
  };

  // Format timestamp
  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();

    if (isToday) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Get friends who don't have conversations yet
  const friendsWithoutConversations = Array.from(friends).filter(
    pubkey => !conversations.some(c => c.peerPubkey === pubkey)
  );

  return (
    <div className="chat-panel">
      <div className="chat-header">
        <h2>Messages</h2>
        <button className="close-btn" onClick={onClose}>×</button>
      </div>

      <div className="chat-body">
        {/* Conversation List */}
        <div className="conversation-list">
          <div className="conversation-list-header">
            <span>Conversations</span>
          </div>

          {conversations.length === 0 && friendsWithoutConversations.length === 0 && (
            <div className="no-conversations">
              <p>No conversations yet</p>
              <p className="hint">Add friends to start messaging</p>
            </div>
          )}

          {/* Existing conversations */}
          {conversations.map(conv => (
            <button
              key={conv.peerPubkey}
              className={`conversation-item ${selectedPeer === conv.peerPubkey ? 'selected' : ''} ${conv.unreadCount > 0 ? 'unread' : ''}`}
              onClick={() => setSelectedPeer(conv.peerPubkey)}
            >
              <div className="avatar-with-status">
                <Avatar
                  src={getAvatarUrl?.(conv.peerPubkey)}
                  displayName={getDisplayName(conv.peerPubkey)}
                  size="small"
                />
                {isOnline?.(conv.peerPubkey) && <span className="online-dot" />}
              </div>
              <div className="conversation-info">
                <div className="conversation-name">{getDisplayName(conv.peerPubkey)}</div>
                {conv.lastMessage && (
                  <div className="conversation-preview">
                    {conv.lastMessage.fromMe && <span className="you">You: </span>}
                    {conv.lastMessage.text.slice(0, 30)}
                    {conv.lastMessage.text.length > 30 && '...'}
                  </div>
                )}
              </div>
              <div className="conversation-meta">
                {conv.lastMessage && (
                  <span className="conversation-time">{formatTime(conv.lastMessage.timestamp)}</span>
                )}
                {conv.unreadCount > 0 && (
                  <span className="unread-badge">{conv.unreadCount}</span>
                )}
              </div>
            </button>
          ))}

          {/* Friends without conversations */}
          {friendsWithoutConversations.length > 0 && (
            <>
              <div className="conversation-list-header">
                <span>Start a conversation</span>
              </div>
              {friendsWithoutConversations.map(pubkey => (
                <button
                  key={pubkey}
                  className="conversation-item new-conversation"
                  onClick={() => handleNewConversation(pubkey)}
                >
                  <div className="avatar-with-status">
                    <Avatar
                      src={getAvatarUrl?.(pubkey)}
                      displayName={getDisplayName(pubkey)}
                      size="small"
                    />
                    {isOnline?.(pubkey) && <span className="online-dot" />}
                  </div>
                  <div className="conversation-info">
                    <div className="conversation-name">{getDisplayName(pubkey)}</div>
                    <div className="conversation-preview new">Start messaging</div>
                  </div>
                </button>
              ))}
            </>
          )}
        </div>

        {/* Chat View */}
        <div className="chat-view">
          {!selectedPeer ? (
            <div className="no-chat-selected">
              <p>Select a conversation to start messaging</p>
            </div>
          ) : (
            <>
              {/* Chat header */}
              <div className="chat-view-header">
                <button className="back-btn" onClick={() => setSelectedPeer(null)}>
                  ←
                </button>
                <div className="avatar-with-status">
                  <Avatar
                    src={getAvatarUrl?.(selectedPeer)}
                    displayName={getDisplayName(selectedPeer)}
                    size="small"
                  />
                  {isOnline?.(selectedPeer) && <span className="online-dot" />}
                </div>
                <div className="chat-peer-info">
                  <div className="chat-peer-name">{getDisplayName(selectedPeer)}</div>
                  <div className="chat-peer-status">
                    {isOnline?.(selectedPeer) ? 'Online' : 'Offline'}
                  </div>
                </div>
                {onStartCall && (
                  <CallButton
                    peerPubkey={selectedPeer}
                    onStartCall={onStartCall}
                  />
                )}
              </div>

              {/* Messages */}
              <div className="messages-container">
                {loadingMessages && messages.length === 0 ? (
                  <div className="loading-messages">Loading messages...</div>
                ) : messages.length === 0 ? (
                  <div className="no-messages">
                    <p>No messages yet</p>
                    <p className="hint">Send a message to start the conversation</p>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.hash}
                      className={`message ${msg.fromMe ? 'sent' : 'received'}`}
                    >
                      {/* Attachments */}
                      {msg.attachments && msg.attachments.length > 0 && (
                        <div className="message-attachments">
                          {msg.attachments.map(hash => (
                            <div key={hash} className="attachment-preview">
                              <span className="attachment-icon">📎</span>
                              <span className="attachment-hash">{hash.slice(0, 8)}...</span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="message-content">
                        <MarkdownContent content={msg.text} />
                      </div>
                      <div className="message-time">{formatTime(msg.timestamp)}</div>
                    </div>
                  ))
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Selected Attachments */}
              {selectedAttachments.length > 0 && (
                <div className="selected-attachments">
                  {selectedAttachments.map(hash => (
                    <div key={hash} className="selected-attachment">
                      {attachmentPreviews.get(hash) ? (
                        <img src={attachmentPreviews.get(hash)} alt="attachment" />
                      ) : (
                        <span className="attachment-placeholder">📎</span>
                      )}
                      <button
                        className="remove-attachment"
                        onClick={() => handleRemoveAttachment(hash)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Attachment Picker */}
              {showAttachmentPicker && (
                <div className="attachment-picker">
                  <div className="attachment-picker-header">
                    <span>Select from clipboard</span>
                    <button onClick={() => setShowAttachmentPicker(false)}>×</button>
                  </div>
                  <div className="attachment-picker-list">
                    {clipboardEntries.filter(e =>
                      e.content.content.type.startsWith('image/') ||
                      e.content.content.type.startsWith('video/') ||
                      e.content.content.type.startsWith('audio/') ||
                      e.content.content.type === 'application/pdf'
                    ).slice(0, 10).map(entry => (
                      <button
                        key={entry.hash}
                        className="attachment-option"
                        onClick={() => handleAddAttachment(entry)}
                        disabled={selectedAttachments.includes(entry.hash)}
                      >
                        <span className="attachment-type-icon">
                          {getTypeIcon(entry.content.content.type)}
                        </span>
                        <span className="attachment-label">
                          {entry.content.meta.label || entry.content.meta.filename || entry.hash.slice(0, 8)}
                        </span>
                      </button>
                    ))}
                    {clipboardEntries.filter(e =>
                      e.content.content.type.startsWith('image/') ||
                      e.content.content.type.startsWith('video/')
                    ).length === 0 && (
                      <div className="no-attachments">No media in clipboard</div>
                    )}
                  </div>
                </div>
              )}

              {/* Input */}
              <div className="chat-input">
                <button
                  className="attachment-btn"
                  onClick={() => setShowAttachmentPicker(!showAttachmentPicker)}
                  title="Add attachment"
                >
                  📎
                </button>
                <textarea
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Type a message..."
                  rows={1}
                  disabled={sending}
                />
                <button
                  className="send-btn"
                  onClick={handleSend}
                  disabled={(!newMessage.trim() && selectedAttachments.length === 0) || sending}
                >
                  {sending ? '...' : 'Send'}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
